import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, authAPI, authStorage, clearAuthStorage } from '../services/api';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  // Patches the in-memory user object (and its authStorage cache) after a
  // self-service preference save that returns the updated User from the
  // backend -- without this, pages that read defaults off `user` only at
  // mount (e.g. Study Management's initial sort state) keep showing the
  // value from this session's login/page-load forever, even after a
  // successful save, until the user logs out and back in.
  updateUser: (patch: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in
    const initializeAuth = async () => {
      // Hub auto-login: handle hub_token synchronously before any auth check
      const _params = new URLSearchParams(window.location.search);
      const _hubToken = _params.get('hub_token');
      if (_hubToken) {
        // Clear any leftover session from before this Hub login (e.g. a prior
        // logout/relogin in the same tab) so storage and the is_hub_session flag
        // can never disagree mid-transition.
        clearAuthStorage();
        // Isolate this tab's session in sessionStorage so it can never clobber another
        // tab's localStorage-based session on this same Suite domain (see authStorage()).
        sessionStorage.setItem('is_hub_session', 'true');
        authStorage().setItem('access_token', _hubToken);
        authStorage().removeItem('user');
        _params.delete('hub_token');
        const _newSearch = _params.toString();
        window.history.replaceState({}, '', window.location.pathname + (_newSearch ? '?' + _newSearch : '') + window.location.hash);
      }

      if (authAPI.isAuthenticated()) {
        try {
          const storedUser = authStorage().getItem('user');
          if (storedUser) {
            setUser(JSON.parse(storedUser));
          } else {
            // Fetch current user from API
            const currentUser = await authAPI.getCurrentUser();
            setUser(currentUser);
            authStorage().setItem('user', JSON.stringify(currentUser));
          }
        } catch (error) {
          console.error('Failed to initialize auth:', error);
          authAPI.logout();
          setUser(null);
        }
      }
      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  const login = async (username: string, password: string): Promise<void> => {
    // Deliberately does not touch isLoading: that flag gates the whole app's
    // route tree (shown as a full-page spinner during the initial session
    // bootstrap above). Toggling it here used to unmount/remount the Login
    // page around every login attempt, wiping the error state Login.tsx had
    // just set before the user could ever see it. Login.tsx has its own
    // local loading state for the submit button.
    try {
      const response = await authAPI.login(username, password);
      setUser(response.user);
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = () => {
    authAPI.logout();
    setUser(null);
  };

  const updateUser = (patch: Partial<User>) => {
    setUser(prev => {
      if (!prev) return prev;
      const next = { ...prev, ...patch };
      authStorage().setItem('user', JSON.stringify(next));
      return next;
    });
  };

  useEffect(() => {
    // Session watchdog. The embedded OHIF viewer (separate app, token/cookie
    // auth on its own orthanc-proxy routes) and the PDF print/view/download
    // paths (raw axios/fetch, not the `api` instance) never reach api.ts's
    // response interceptor, so an expired token there never triggers the
    // auto-redirect to /login -- the user is left stuck in a half-dead app
    // instead. Poll an interceptor-covered endpoint in the background so a
    // dead session gets caught regardless of what the user happens to be
    // doing (including sitting idle in the viewer or a PDF dialog).
    if (!user) return;
    const interval = setInterval(() => {
      authAPI.getCurrentUser().catch(() => {
        // A 401 here is already handled by api.ts's response interceptor
        // (clears storage, redirects to /login) -- just swallow the
        // rejection so it doesn't surface as an unhandled promise.
      });
    }, 60000);
    return () => clearInterval(interval);
  }, [user]);

  const value: AuthContextType = {
    user,
    isLoading,
    login,
    logout,
    isAuthenticated: !!user,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
